import React, { useState, useEffect, createContext, useContext } from 'react';
import { getCurrentUser, signIn, signOut, signUp, confirmSignUp, confirmSignIn } from '../lib/amplify';
import { fetchUserAttributes } from 'aws-amplify/auth';
import type { SignInInput, SignUpInput, ConfirmSignUpInput, SignInOutput } from 'aws-amplify/auth';

interface User {
  username: string;
  email?: string;
  isApproved?: boolean;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  signIn: (input: SignInInput) => Promise<SignInOutput>;
  signUp: (input: SignUpInput) => Promise<void>;
  confirmSignUp: (input: ConfirmSignUpInput) => Promise<void>;
  signOut: () => Promise<void>;
  refreshUser: () => Promise<User | null>;
  completeNewPassword: (newPassword: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

type AmplifyAuthError = Error & {
  name?: string;
  code?: string;
};

const formatAuthError = (err: unknown): string => {
  if (!err) {
    return 'An unknown error occurred. Please try again.';
  }

  if (
    err instanceof Error &&
    err.message &&
    (err.name === 'Error' || err.name === undefined)
  ) {
    const normalized = err.message.trim();
    if (normalized.length > 0 && normalized !== 'No current user') {
      return normalized;
    }
  }

  const error = err as AmplifyAuthError;
  const code = (error.name || error.code || '').trim();

  switch (code) {
    case 'UserNotConfirmedException':
      return 'Your email address has not been confirmed yet.';
    case 'NotAuthorizedException':
    case 'UserNotFoundException':
      return 'Incorrect email or password.';
    case 'PasswordResetRequiredException':
      return 'You must reset your password before signing in.';
    case 'TooManyFailedAttemptsException':
    case 'TooManyRequestsException':
      return 'Too many failed attempts. Please wait a moment and try again.';
    default:
      return 'Unable to sign in. Please verify your credentials and try again.';
  }
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refreshUser = async (): Promise<User | null> => {
    try {
      setLoading(true);
      const currentUser = await getCurrentUser();
      const userAttributes = await fetchUserAttributes();

      const nextUser: User = {
        username: currentUser.username,
        email: userAttributes.email,
        isApproved: userAttributes['custom:isApproved'] === 'true',
      };

      setUser(nextUser);
      setError(null);
      return nextUser;
    } catch (err) {
      setUser(null);
      if (err instanceof Error && err.message.toLowerCase().includes('current user')) {
        setError(null);
        return null;
      }
      const message = formatAuthError(err);
      setError(message);
      return null;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshUser();
  }, []);

  const handleSignIn = async (input: SignInInput): Promise<SignInOutput> => {
    try {
      setLoading(true);
      setError(null);
      const response = await signIn(input);

      if (response.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        return response;
      }

      const refreshedUser = await refreshUser();
      if (refreshedUser && !refreshedUser.isApproved) {
        await signOut();
        const pendingMessage = 'Your account is pending activation. Please contact an administrator.';
        setError(pendingMessage);
        throw new Error(pendingMessage);
      }
      return response;
    } catch (err) {
      const message = formatAuthError(err);
      setError(message);
      throw new Error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteNewPassword = async (newPassword: string) => {
    try {
      setLoading(true);
      setError(null);
      await confirmSignIn({ challengeResponse: newPassword });

      const refreshedUser = await refreshUser();
      if (refreshedUser && !refreshedUser.isApproved) {
        await signOut();
        const pendingMessage = 'Your account is pending activation. Please contact an administrator.';
        setError(pendingMessage);
        throw new Error(pendingMessage);
      }
    } catch (err) {
      const message = formatAuthError(err);
      setError(message);
      throw new Error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (input: SignUpInput) => {
    try {
      setLoading(true);
      setError(null);
      await signUp(input);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign up failed');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmSignUp = async (input: ConfirmSignUpInput) => {
    try {
      setLoading(true);
      setError(null);
      await confirmSignUp(input);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Confirmation failed');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    try {
      setLoading(true);
      setError(null);
      await signOut();
      setUser(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign out failed');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const value = {
    user,
    loading,
    error,
    signIn: handleSignIn,
    signUp: handleSignUp,
    confirmSignUp: handleConfirmSignUp,
    signOut: handleSignOut,
    refreshUser,
    completeNewPassword: handleCompleteNewPassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
